import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, categories } from "@/mocks/data";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/category"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer text-sm font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            author: {
              "@type": "Person",
              name: article.author,
            },
            datePublished: article.date,
            url: `https://www.aiqingdao.com/article/${article.slug}`,
            publisher: {
              "@type": "Organization",
              name: "爱情岛网站",
            },
            keywords: article.tags.join(", "),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://www.aiqingdao.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: article.categoryName,
                item: `https://www.aiqingdao.com/category?cat=${categories.find((c) => c.name === article.categoryName)?.slug || ""}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="container-page max-w-3xl">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <Link
                to={`/category?cat=${categories.find((c) => c.name === article.categoryName)?.slug || ""}`}
                className="hover:text-primary-500 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <span>/</span>
              <span className="text-foreground-900 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-600">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-5">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.views.toLocaleString()} 次阅读</span>
              </div>
            </header>

            <div className="prose-custom">
              {article.content.map((section, idx) => {
                if (section.type === "heading") {
                  const HeadingTag = `h${Math.min(idx + 2, 6)}` as keyof JSX.IntrinsicElements;
                  return (
                    <HeadingTag
                      key={idx}
                      className="text-xl font-heading font-semibold text-foreground-900 mt-10 mb-4"
                    >
                      {section.content}
                    </HeadingTag>
                  );
                }
                if (section.type === "paragraph") {
                  return (
                    <p
                      key={idx}
                      className="text-base text-foreground-700 leading-relaxed mb-5"
                    >
                      {section.content}
                    </p>
                  );
                }
                if (section.type === "list" && section.items) {
                  return (
                    <div key={idx} className="mb-6">
                      <p className="text-base text-foreground-700 mb-3">{section.content}</p>
                      <ul className="list-disc pl-6 space-y-2">
                        {section.items.map((item, i) => (
                          <li key={i} className="text-base text-foreground-700 leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <Link
                  to="/category"
                  className="whitespace-nowrap inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回文章列表
                </Link>
                <div className="flex items-center gap-4">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer" aria-label="分享">
                    <i className="ri-share-forward-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 hover:border-primary-200 transition-colors cursor-pointer" aria-label="收藏">
                    <i className="ri-bookmark-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="container-page">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2.5 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {ra.excerpt}
                    </p>
                    <div className="text-xs text-foreground-500">{ra.date}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

